import { useState } from 'react';
import { Grid, Card, CardContent, CardActions, Typography, Button, Chip, Stack, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { useAuth } from '../../hooks/useAuth';
import { integrationApi, type ConnectorMeta } from '../../api/integration.api';

export function MarketplacePage() {
  const { hasPermission } = useAuth();
  const [connect, setConnect] = useState<ConnectorMeta | null>(null);
  const { data } = useQuery({ queryKey: ['integration-marketplace'], queryFn: () => integrationApi.marketplace() });

  return (
    <>
      <PageHeader title="Integration Marketplace" subtitle="Pre-built connectors for client systems, logistics, ERP and OEM/CPO platforms." />
      <Grid container spacing={2}>
        {(data ?? []).map((c) => (
          <Grid item xs={12} sm={6} md={4} key={c.key}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle1" fontWeight={600}>{c.label}</Typography>
                  <Chip size="small" label={c.category.toUpperCase()} />
                </Stack>
                <Typography variant="body2" color="text.secondary" mb={1}>{c.description}</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {c.transports.map((t) => <Chip key={t} size="small" variant="outlined" label={t} />)}
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>Auth: {c.authTypes.join(', ')}</Typography>
                {c.actions.length > 0 && <Typography variant="caption" color="text.secondary" display="block">Actions: {c.actions.join(', ')}</Typography>}
              </CardContent>
              <CardActions>
                <Button size="small" variant="contained" disabled={!hasPermission('integration.create')} onClick={() => setConnect(c)}>Connect</Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      {connect && <ConnectDialog connector={connect} onClose={() => setConnect(null)} />}
    </>
  );
}

function ConnectDialog({ connector, onClose }: { connector: ConnectorMeta; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState(connector.label);
  const create = useMutation({
    mutationFn: () => integrationApi.create({ name, category: connector.category, connectorKey: connector.key, authType: connector.authTypes[0], transport: connector.transports[0] }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['integrations'] }); onClose(); navigate('/integrations'); },
  });
  return (
    <Dialog open fullWidth maxWidth="xs" onClose={onClose}>
      <DialogTitle>Connect {connector.label}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Alert severity="info">Creates a draft integration. Configure credentials and activate it from the Integrations page.</Alert>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!name || create.isPending} onClick={() => create.mutate()}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}
